import { useEffect } from 'react';
import { container } from 'tsyringe';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import IntegrationsPageLayout from '@/features/integrations/IntegrationsPageLayout';
import { AiProvidersPageViewModel } from '@/features/integrations/AiProvidersPageViewModel';
import {
    AWS_BEDROCK_PROVIDER_TYPE_ID,
    AWS_BEDROCK_BUILD_LAUNCH_URL_ROUTE,
    buildAwsBedrockLaunchUrl,
} from '@/features/integrations/wizard/AwsBedrockLaunchUrlBuilder';
import type { LaunchUrlResolver } from '@/features/integrations/wizard/ConnectionStepAuth';
import { RuntimeConfigToken, type RuntimeConfig } from '@/runtime-config/RuntimeConfig';

export default function AiProvidersPage(): React.ReactElement {
    const vm = useViewModel(AiProvidersPageViewModel);

    useEffect(() => {
        void vm.load();
    }, [vm]);

    return (
        <IntegrationsPageLayout
            vm={vm}
            title="AI Providers"
            subtitle="Connect this tenant to external AI providers to ingest usage telemetry."
            addLabel="Add AI provider"
            emptyTitle="No AI providers connected"
            emptyDescription="Connect a provider like OpenAI or Anthropic to ingest usage data."
            onResolveLaunchUrl={resolveLaunchUrl}
        />
    );
}

const resolveLaunchUrl: LaunchUrlResolver = (providerTypeId, route, config) => {
    if (providerTypeId !== AWS_BEDROCK_PROVIDER_TYPE_ID) return null;
    if (route !== AWS_BEDROCK_BUILD_LAUNCH_URL_ROUTE) return null;
    const runtimeConfig = container.resolve<RuntimeConfig>(RuntimeConfigToken);
    const bedrock = runtimeConfig.integrations.awsBedrock;
    const externalId = typeof config['externalId'] === 'string' ? config['externalId'] : '';
    return buildAwsBedrockLaunchUrl({
        cfnTemplateUrl: bedrock.cfnTemplateUrl,
        platformAccountId: bedrock.platformAccountId,
        cloudFormationStackName: bedrock.cloudFormationStackName,
        consoleHostTemplate: bedrock.consoleHostTemplate,
        externalId,
    });
};
